import { Component } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";

import { addSettings } from "../../../actions";
import {
  createApiKeyV1,
  listApiKeysV1,
  disableApiKeyV1,
  enableApiKeyV1,
  deleteApiKeyV1,
  rotateApiKeyV1,
  listSubClients,
} from "../../../config/client_api_calls";
import DataTableExtended from "../../../components/Tables/DataTableExtended";
import { TableLoader } from "../../../components/ContentLoaders/newLoaders/TableLoader";
import MsaWarning from "../ExtraComponents/MsaWarning";
import SettingsError from "../ExtraComponents/SettingsError";
import PageTitle from "../ExtraComponents/PageTitle";
import HelperClass from "../../../config/helperClass";
import MutextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { FormLoader } from "../../../components/ContentLoaders/newLoaders/FormLoader";

const auth = new HelperClass();

// Client API v1 API keys - external systems use these with
// POST /api/v1/auth/token (grant_type: client_credentials) to access the
// whole /api/v1/* surface. A SEPARATE page from "Api Key" (the existing
// Zapier-webhook-only key system) on purpose - different trust boundary,
// not worth mixing in one table.
class ApiKeysV1 extends Component {
  constructor() {
    super();

    this.state = {
      tableData: [],
      tableLoader: true,
      subClients: [],

      errorMessage: "",
      successMessage: "",
      errorMessageDelete: "",
      successMessageDelete: "",
      errorMessageRotate: "",
      successMessageRotate: "",

      label: "",
      for_accountno: "",
      api_key: "",
      apiCopied: false,
      selected_key_id: "",
      disabled: false,
      isProcessing: false,

      // Row-actions menu, rendered via a React portal straight to
      // document.body (see render's ReactDOM.createPortal call) - not
      // Bootstrap's own dropdown toggle. The table needs horizontal scroll
      // for its column count, and that scrolling wrapper clips anything
      // rendering below a row's height; position: fixed alone isn't a
      // reliable fix for that, since a transformed ancestor (common in
      // Material-UI's elevation/shadow styling) creates a new containing
      // block and defeats fixed positioning too. A portal sidesteps the
      // whole question - the menu DOM node lives outside the table
      // entirely, so no ancestor's overflow/transform can clip it.
      openActionsRowId: null,
      actionsMenuPos: { top: 0, right: 0 },

      columnServices: [
        { name: "Label" },
        { name: "Account No" },
        {
          name: "Key",
          options: {
            customBodyRender: (value) => <div>{value}</div>,
          },
        },
        {
          name: "Status",
          options: {
            customBodyRender: (value) => (
              <span
                className={`badge badge-outline-${
                  value === "active" ? "success" : "warning"
                }`}
              >
                {value === "active" ? "Active" : "Disabled"}
              </span>
            ),
          },
        },
        {
          name: "Created",
          options: {
            customBodyRender: (value) => <div>{this.formatDateTime(value)}</div>,
          },
        },
        {
          name: "Expires",
          options: {
            customBodyRender: (value) => (
              <div>{value ? this.formatDateTime(value) : "Never"}</div>
            ),
          },
        },
        {
          name: "Last Used",
          options: {
            customBodyRender: (value) => (
              <div>{value ? this.formatDateTime(value) : "Never"}</div>
            ),
          },
        },
        {
          name: "Actions",
          options: {
            customBodyRender: (value, tableMeta) => {
              const keyId = tableMeta.rowData[7];
              const status = tableMeta.rowData[3];
              const isOpen = this.state.openActionsRowId === keyId;
              return (
                <div className="nk-tb-col nk-tb-col-tools" style={{ padding: "0px" }}>
                  <ul className="nk-tb-actions gx-1">
                    <li>
                      <a
                        href="#"
                        title="Other Actions"
                        className="btn btn-icon btn-outline-light"
                        onClick={(e) => this.toggleActionsMenu(e, keyId)}
                      >
                        <em className="icon ni ni-more-h"></em>
                      </a>
                      {isOpen && this.renderActionsMenu(keyId, status)}
                    </li>
                  </ul>
                </div>
              );
            },
          },
        },
      ],
    };
  }

  // Portal target: a direct child of document.body, so it's outside the
  // table's DOM subtree entirely - immune to the table's horizontal-scroll
  // wrapper clipping it, regardless of overflow/transform on any ancestor.
  renderActionsMenu(keyId, status) {
    const closeThen = (fn) => () => {
      this.closeActionsMenu();
      fn(keyId);
    };
    return ReactDOM.createPortal(
      <div
        className="dropdown-menu show apikeysv1-portal-menu"
        style={{
          position: "fixed",
          top: this.state.actionsMenuPos.top,
          right: this.state.actionsMenuPos.right,
          left: "auto",
          zIndex: 2000,
          display: "block",
        }}
      >
        <ul className="link-list-opt no-bdr">
          <li>
            <a
              style={{ cursor: "pointer" }}
              onClick={closeThen(status === "active" ? this.disableKey : this.enableKey)}
            >
              <em className={`icon ni ${status === "active" ? "ni-na" : "ni-done"}`}></em>
              <span>{status === "active" ? "Disable" : "Enable"}</span>
            </a>
          </li>
          <li>
            <a style={{ cursor: "pointer" }} onClick={closeThen(this.rotateKeyFun)}>
              <em className="icon ni ni-reload"></em>
              <span>Rotate</span>
            </a>
          </li>
          <li>
            <a style={{ cursor: "pointer" }} onClick={closeThen(this.deleteKeyFun)}>
              <em className="icon ni ni-trash"></em>
              <span>Delete</span>
            </a>
          </li>
        </ul>
      </div>,
      document.body
    );
  }

  toggleActionsMenu = (e, keyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.openActionsRowId === keyId) {
      this.setState({ openActionsRowId: null });
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    this.setState({
      openActionsRowId: keyId,
      actionsMenuPos: { top: rect.bottom + 4, right: window.innerWidth - rect.right },
    });
  };

  closeActionsMenu = () => {
    this.setState({ openActionsRowId: null });
  };

  handleOutsideActionsClick = (e) => {
    if (!this.state.openActionsRowId) return;
    if (e.target.closest && e.target.closest(".apikeysv1-portal-menu")) return;
    this.setState({ openActionsRowId: null });
  };

  async componentDidMount() {
    await this.getApiKeys();
    const subRes = await listSubClients(auth.getClientAccount(), auth.getClientToken());
    if (subRes.data && subRes.data.status === 200) {
      this.setState({ subClients: subRes.data.data || [] });
    }

    // Portal-rendered menu (see renderActionsMenu): since it's no longer a
    // Bootstrap-managed dropdown, outside-click-to-close and close-on-scroll
    // (the computed position would otherwise go stale) are handled here
    // instead of relying on Bootstrap's own dismissal behavior.
    document.addEventListener("mousedown", this.handleOutsideActionsClick);
    window.addEventListener("scroll", this.closeActionsMenu, true);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideActionsClick);
    window.removeEventListener("scroll", this.closeActionsMenu, true);
  }

  formatDateTime(dateString) {
    if (!dateString || isNaN(Date.parse(dateString))) return "Invalid Date";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleString("en-US", options);
  }

  rowsFromKeys(keys) {
    // 8 elements, matching columnServices' 8 declared columns exactly - id
    // sits at the Actions column's own index (7), never past the declared
    // column count. tableMeta.rowData appears to be truncated to
    // columns.length by the table component, so an id placed one index
    // beyond that (as an earlier version of this file did) read back as
    // undefined, which JSON.stringify then silently drops from the request
    // body - the actual cause of a prior "key_id: Invalid value" bug.
    return keys.map((k) => [
      k.label,
      k.accountno,
      `${k.key_prefix}••••${k.key_last4}`,
      k.status,
      k.created_at,
      k.expires_at,
      k.last_used_at,
      k.id,
    ]);
  }

  getApiKeys = async () => {
    const res = await listApiKeysV1(auth.getClientAccount(), auth.getClientToken());
    if (
      !res.data ||
      res.data.status === 403 ||
      res.data.errors === "authentication missing" ||
      res.data.errors === "jwt expired"
    ) {
      auth.clientLogout();
    } else if (res.data.status === 200) {
      this.setState({ tableData: this.rowsFromKeys(res.data.data || []), tableLoader: false });
    } else {
      this.setState({ tableData: [], tableLoader: false });
    }
  };

  handleChanges = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  openCreateModal = () => {
    this.setState({ errorMessage: "", successMessage: "", label: "", for_accountno: "" });
    window.$("#modalAddNewApiKeyV1").modal("show");
  };

  modalHideCreate = () => {
    window.$("#modalAddNewApiKeyV1").modal("hide");
  };

  saveNewApiKey = async () => {
    const { label, for_accountno } = this.state;
    this.setState({ errorMessage: "", successMessage: "", disabled: true });

    if (label === "") {
      this.setState({ errorMessage: "Please enter a label.", disabled: false });
      return;
    }

    const res = await createApiKeyV1(
      auth.getClientAccount(),
      auth.getClientToken(),
      label,
      for_accountno || undefined
    );

    if (
      !res.data ||
      res.data.status === 403 ||
      res.data.errors === "authentication missing" ||
      res.data.errors === "jwt expired"
    ) {
      auth.clientLogout();
    } else if (res.data.status === 429) {
      this.setState({ errorMessage: res.data.message, disabled: false });
    } else if (res.data.status === 200) {
      this.setState({
        errorMessage: "",
        successMessage: "API key created successfully.",
        disabled: false,
        label: "",
        api_key: res.data.data.api_key,
      });
      setTimeout(() => window.$("#modalAddNewApiKeyV1").modal("hide"), 2000);
      setTimeout(() => {
        window.$("#modalShowApiKeyV1").modal("show");
        this.getApiKeys();
      }, 2200);
    } else {
      this.setState({
        errorMessage: res.data.message || "There was an error creating the API key.",
        disabled: false,
      });
    }
  };

  copyApiKeyToClipboard = () => {
    const input = document.getElementById("copyApiKeyV1Input");
    input.select();
    input.setSelectionRange(0, 99999);
    document.execCommand("copy");
    this.setState({ apiCopied: true });
    setTimeout(() => this.setState({ apiCopied: false }), 2000);
  };

  disableKey = async (key_id) => {
    const res = await disableApiKeyV1(auth.getClientAccount(), auth.getClientToken(), key_id);
    if (res.data && res.data.status === 200) this.getApiKeys();
  };

  enableKey = async (key_id) => {
    const res = await enableApiKeyV1(auth.getClientAccount(), auth.getClientToken(), key_id);
    if (res.data && res.data.status === 200) this.getApiKeys();
  };

  deleteKeyFun = (key_id) => {
    this.setState({ selected_key_id: key_id, errorMessageDelete: "", successMessageDelete: "" });
    window.$("#modalDeleteApiKeyV1").modal("show");
  };

  modalHideDelete = () => {
    window.$("#modalDeleteApiKeyV1").modal("hide");
  };

  deleteKey = async () => {
    this.setState({ isProcessing: true });
    const res = await deleteApiKeyV1(
      auth.getClientAccount(),
      auth.getClientToken(),
      this.state.selected_key_id
    );
    if (res.data && res.data.status === 200) {
      this.setState({ successMessageDelete: "API key deleted.", isProcessing: false });
      setTimeout(() => {
        window.$("#modalDeleteApiKeyV1").modal("hide");
        this.getApiKeys();
      }, 1500);
    } else {
      this.setState({
        errorMessageDelete: (res.data && res.data.message) || "Failed to delete API key.",
        isProcessing: false,
      });
    }
  };

  rotateKeyFun = (key_id) => {
    this.setState({ selected_key_id: key_id, errorMessageRotate: "", successMessageRotate: "" });
    window.$("#modalRotateApiKeyV1").modal("show");
  };

  modalHideRotate = () => {
    window.$("#modalRotateApiKeyV1").modal("hide");
  };

  rotateKey = async () => {
    this.setState({ isProcessing: true });
    const res = await rotateApiKeyV1(
      auth.getClientAccount(),
      auth.getClientToken(),
      this.state.selected_key_id
    );
    if (res.data && res.data.status === 200) {
      this.setState({ isProcessing: false, api_key: res.data.data.api_key });
      window.$("#modalRotateApiKeyV1").modal("hide");
      setTimeout(() => {
        window.$("#modalShowApiKeyV1").modal("show");
        this.getApiKeys();
      }, 200);
    } else {
      this.setState({
        errorMessageRotate: (res.data && res.data.message) || "Failed to rotate API key.",
        isProcessing: false,
      });
    }
  };

  render() {
    return (
      <div className="nk-content" id="ApiKeysV1_Block">
        <div className="container-fluid">
          {this.props.defaultSettings === "missing" && this.props.from !== "dashboard" ? (
            <SettingsError />
          ) : null}
          {this.props.is_msa_signed === "No" && this.props.from !== "dashboard" ? (
            <MsaWarning />
          ) : null}
          <div className="nk-content-inner">
            <div className="nk-content-body">
              <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                  <div className="nk-block-head-content">
                    <h3 className="nk-block-title page-title">
                      <PageTitle name="API Keys" icon="icon ni ni-setting" />
                    </h3>
                  </div>
                  <div className="nk-block-head-content" id="HeadContent">
                    <div className="toggle-wrap nk-block-tools-toggle">
                      <a
                        href="#"
                        className="btn btn-icon btn-trigger toggle-expand mr-n1"
                        data-target="pageMenu"
                      >
                        <em className="icon ni ni-more-v"></em>
                      </a>
                      <div className="toggle-expand-content" data-content="pageMenu">
                        <ul className="nk-block-tools g-3">
                          <li className="nk-block-tools-opt">
                            {this.props.defaultSettings !== "missing" ? (
                              <a
                                onClick={() => this.openCreateModal()}
                                style={{ color: "#fff" }}
                                className="btn btn-primary"
                              >
                                <em className="icon ni ni-plus-round"></em>
                                <span>Create API Key</span>
                              </a>
                            ) : null}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="nk-block">
                <div className="row g-gs">
                  <div className="col-xxl-12">
                    <div className="nk-block nk-block-lg">
                      <div className="card card-bordered">
                        <div className="card-inner">
                          {this.state.tableLoader ? (
                            TableLoader()
                          ) : (
                            <DataTableExtended
                              columns={this.state.columnServices}
                              tableData={this.state.tableData}
                              title=""
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CREATE MODAL */}
                  <div className="modal fade" tabIndex="-1" id="modalAddNewApiKeyV1">
                    <div className="modal-dialog modal-dialog-top modal-xl" role="document">
                      <div className="modal-content">
                        <a href="#" className="close" data-dismiss="modal" aria-label="Close">
                          <em className="icon ni ni-cross"></em>
                        </a>
                        <div className="modal-header">
                          <h5 className="modal-title">
                            <em className="icon ni ni-setting" style={{ marginRight: "15px" }}></em>
                            Create a new API Key
                          </h5>
                        </div>
                        <div className="modal-body">
                          {this.state.formLoader ? (
                            FormLoader()
                          ) : (
                            <div className="row g-4">
                              {this.state.errorMessage !== "" ? (
                                <div className="col-md-12 mb-1">
                                  <div className="example-alert example-alert-revoke" style={{ marginBottom: "15px" }}>
                                    <div className="alert alert-pro alert-danger">
                                      <div className="alert-text">
                                        <h4>Error</h4>
                                        <p>{this.state.errorMessage}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              {this.state.successMessage !== "" ? (
                                <div className="col-md-12 mb-1">
                                  <div className="example-alert example-alert-revoke" style={{ marginBottom: "15px" }}>
                                    <div className="alert alert-pro alert-success">
                                      <div className="alert-text">
                                        <h4>Success</h4>
                                        <p>{this.state.successMessage}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              <div className="col-md-12">
                                <div className="form-group">
                                  <div className="form-control-group">
                                    <label htmlFor="label" className="form-label">
                                      Label *
                                    </label>
                                    <MutextField
                                      required
                                      id="label"
                                      name="label"
                                      type="text"
                                      value={this.state.label}
                                      inputProps={{ maxLength: 191 }}
                                      onChange={this.handleChanges}
                                      variant="outlined"
                                      fullWidth
                                    />
                                  </div>
                                </div>
                              </div>

                              {this.state.subClients.length > 0 ? (
                                <div className="col-md-12">
                                  <div className="form-group">
                                    <div className="form-control-group">
                                      <label htmlFor="for_accountno" className="form-label">
                                        For
                                      </label>
                                      <select
                                        id="for_accountno"
                                        name="for_accountno"
                                        className="form-control"
                                        value={this.state.for_accountno}
                                        onChange={this.handleChanges}
                                      >
                                        <option value="">My own account</option>
                                        {this.state.subClients.map((sc) => (
                                          // list_sub_clients returns positional arrays, not
                                          // objects: [NAME, COMPANY_NAME, PHONE, email,
                                          // register_date, account_status, ID, ...] -
                                          // client_portal.js:8510-8528. sc[0]=name, sc[6]=accountno.
                                          <option key={sc[6]} value={sc[6]}>
                                            {sc[0]} — {sc[6]}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              <div className="col-md-12 d-flex justify-content-end">
                                <button
                                  type="button"
                                  onClick={() => this.modalHideCreate()}
                                  className="btn btn-lg btn-danger mr-2"
                                  disabled={this.state.disabled}
                                >
                                  <span>Cancel</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => this.saveNewApiKey()}
                                  className="btn btn-lg btn-primary"
                                  disabled={this.state.disabled}
                                >
                                  <span>Create</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SHOW KEY MODAL (shared: create + rotate) */}
                  <div className="modal fade" id="modalShowApiKeyV1" tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Copy and save the API key</h5>
                          <button
                            type="button"
                            className="close"
                            data-dismiss="modal"
                            aria-label="Close"
                            style={{ outline: "none", boxShadow: "none", border: "none", background: "transparent" }}
                          >
                            <span aria-hidden="true">&times;</span>
                          </button>
                        </div>
                        <div className="modal-body">
                          <label className="form-label">The API key</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={this.state.api_key}
                              readOnly
                              id="copyApiKeyV1Input"
                            />
                            <div className="input-group-append">
                              <Tooltip title={this.state.apiCopied ? "Copied!" : "Copy"} placement="top">
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => this.copyApiKeyToClipboard()}
                                >
                                  {this.state.apiCopied ? "Copied!" : <em className="icon ni ni-copy"></em>}
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                          <small className="text-muted d-block mt-2">
                            For security, this key will not be shown again. Store it securely
                            now.
                          </small>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-primary" data-dismiss="modal">
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* DELETE MODAL */}
                  <div className="modal fade" tabIndex="-1" id="modalDeleteApiKeyV1">
                    <div className="modal-dialog" role="document">
                      <div className="modal-content">
                        <a
                          style={{ cursor: "pointer" }}
                          className="close"
                          onClick={() => this.modalHideDelete()}
                        >
                          <em className="icon ni ni-cross"></em>
                        </a>
                        <div className="modal-body modal-body-lg text-center">
                          <div className="nk-modal">
                            <em className="nk-modal-icon icon icon-circle icon-circle-xxl ni ni-trash bg-success"></em>
                            <h4 className="nk-modal-title">Delete API Key?</h4>
                            <div className="nk-modal-text">
                              {this.state.errorMessageDelete !== "" ? (
                                <div className="example-alert" style={{ marginBottom: "15px" }}>
                                  <div className="alert alert-pro alert-danger">
                                    <div className="alert-text">
                                      <h4>Error</h4>
                                      <p>{this.state.errorMessageDelete}</p>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              {this.state.successMessageDelete !== "" ? (
                                <div className="example-alert" style={{ marginBottom: "15px" }}>
                                  <div className="alert alert-pro alert-success">
                                    <div className="alert-text">
                                      <h4>Success</h4>
                                      <p>{this.state.successMessageDelete}</p>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              <div className="caption-text">
                                Any system using this key will stop being able to authenticate
                                immediately. You'll have to create a new key.
                              </div>
                            </div>
                            <div className="nk-modal-action">
                              <a
                                style={{ cursor: "pointer", color: "white" }}
                                className="btn btn-lg btn-mw btn-primary"
                                onClick={() => this.modalHideDelete()}
                              >
                                CANCEL
                              </a>{" "}
                              <a
                                style={{ cursor: "pointer", color: "white" }}
                                className="btn btn-lg btn-mw btn-danger"
                                onClick={() => this.deleteKey()}
                                disabled={this.state.isProcessing}
                              >
                                {this.state.isProcessing ? <span>DELETING...</span> : <span>DELETE</span>}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ROTATE MODAL */}
                  <div className="modal fade" tabIndex="-1" id="modalRotateApiKeyV1">
                    <div className="modal-dialog" role="document">
                      <div className="modal-content">
                        <a
                          style={{ cursor: "pointer" }}
                          className="close"
                          onClick={() => this.modalHideRotate()}
                        >
                          <em className="icon ni ni-cross"></em>
                        </a>
                        <div className="modal-body modal-body-lg text-center">
                          <div className="nk-modal">
                            <em className="nk-modal-icon icon icon-circle icon-circle-xxl ni ni-reload bg-success"></em>
                            <h4 className="nk-modal-title">Rotate API Key?</h4>
                            <div className="nk-modal-text">
                              {this.state.errorMessageRotate !== "" ? (
                                <div className="example-alert" style={{ marginBottom: "15px" }}>
                                  <div className="alert alert-pro alert-danger">
                                    <div className="alert-text">
                                      <h4>Error</h4>
                                      <p>{this.state.errorMessageRotate}</p>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              <div className="caption-text">
                                The current key will stop working immediately and a new one will be
                                generated. Update any system using the old key.
                              </div>
                            </div>
                            <div className="nk-modal-action">
                              <a
                                style={{ cursor: "pointer", color: "white" }}
                                className="btn btn-lg btn-mw btn-primary"
                                onClick={() => this.modalHideRotate()}
                              >
                                CANCEL
                              </a>{" "}
                              <a
                                style={{ cursor: "pointer", color: "white" }}
                                className="btn btn-lg btn-mw btn-danger"
                                onClick={() => this.rotateKey()}
                                disabled={this.state.isProcessing}
                              >
                                {this.state.isProcessing ? <span>ROTATING...</span> : <span>ROTATE</span>}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    defaultSettings: state.settings,
    is_msa_signed: state.is_msa_signed,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    addSettings: (payload) => dispatch(addSettings(payload)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(ApiKeysV1);
